import React, { useEffect, useRef, useState } from 'react';
import { api, CustomField, FieldKind } from './api';
import { Select } from './Select';
import { DateTimePicker } from './DateTimePicker';
import { useAuth } from './ChatAuth';
import { Icon } from './icons';
import { avatarStyle } from './AccountMenu';

type Tab = 'account' | 'status' | 'about';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'account', label: 'Profile', icon: 'smiley' },
  { key: 'status', label: 'Status & availability', icon: 'bell' },
  { key: 'about', label: 'About', icon: 'bolt' },
];

const MODE_LABEL: Record<string, string> = { active: 'Active', focus: 'Focus', async: 'Async only', dnd: 'Do not disturb' };
const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']; // index 0=Sun .. 6=Sat

const CAL_PROVIDERS: { key: 'google' | 'microsoft'; label: string }[] = [
  { key: 'google', label: 'Google Calendar' },
  { key: 'microsoft', label: 'Microsoft Outlook' },
];

/** Connect / disconnect external calendars. Connect redirects the browser to the provider's
 *  consent page; on return the app reads ?calendar=connected. */
function CalendarConnections() {
  const [connected, setConnected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    let alive = true;
    api.calendarStatus()
      .then((r) => { if (alive) setConnected(r.connected.map((c) => c.provider)); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  const connect = async (provider: 'google' | 'microsoft') => {
    setBusy(provider);
    try {
      const { authorize_url } = await api.calendarConnect(provider);
      window.location.href = authorize_url; // full redirect to consent; returns via ?calendar=
    } catch (e: any) {
      window.alert(e?.message || 'Could not start the calendar connection.');
      setBusy('');
    }
  };
  const disconnect = async (provider: 'google' | 'microsoft', label: string) => {
    if (!window.confirm(`Disconnect ${label}?`)) return;
    setBusy(provider);
    try {
      await api.calendarDisconnect(provider);
      setConnected((c) => c.filter((p) => p !== provider));
    } catch { /* ignore */ } finally { setBusy(''); }
  };
  return (
    <>
      <div className="fx-set-row-desc" style={{ marginBottom: 8 }}>
        Connect a calendar so “find a time” sees your real availability (and, soon, schedules meetings for you).
      </div>
      {CAL_PROVIDERS.map((p) => {
        const on = connected.includes(p.key);
        return (
          <div className="fx-set-row" key={p.key}>
            <div className="fx-set-row-main">
              <div className="fx-set-row-title">{p.label}</div>
              <div className="fx-set-row-desc">{loading ? '…' : on ? 'Connected' : 'Not connected'}</div>
            </div>
            <div className="fx-set-row-ctrl">
              {on ? (
                <button type="button" className="fx-login-back" onClick={() => disconnect(p.key, p.label)} disabled={busy === p.key}>Disconnect</button>
              ) : (
                <button type="button" className="fx-sendbtn" onClick={() => connect(p.key)} disabled={!!busy}>{busy === p.key ? 'Connecting…' : 'Connect'}</button>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
const minToTime = (m?: number | null) =>
  m == null ? '' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const timeToMin = (t: string): number | null => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};
const expiryToIso = (preset: string): string | null => {
  if (!preset) return null;
  const now = new Date();
  if (preset === 'today') {
    const d = new Date();
    d.setHours(18, 0, 0, 0);
    return d > now ? d.toISOString() : null;
  }
  return new Date(now.getTime() + Number(preset) * 60000).toISOString();
};

export const KIND_LABEL: Record<FieldKind, string> = { text: 'Text', select: 'Single choice', tags: 'Multiple tags' };
export const fldLink: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--fx-accent,#f7b500)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', padding: '2px 4px' };
export const fldChip: React.CSSProperties = { border: '1px solid var(--fx-hair)', borderRadius: 20, padding: '4px 11px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', background: 'transparent', color: 'var(--fx-text)' };

/** Admin-defined option pool editor (used both when creating a field and when editing one). */
export function OptionEditor({ options, onChange }: { options: string[]; onChange: (o: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !options.some((o) => o.toLowerCase() === v.toLowerCase())) onChange([...options, v]);
    setDraft('');
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 6 }}>
      {options.map((o) => (
        <span key={o} style={{ ...fldChip, cursor: 'default', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {o}<span style={{ cursor: 'pointer', color: 'var(--fx-muted)' }} onClick={() => onChange(options.filter((x) => x !== o))}>×</span>
        </span>
      ))}
      <input className="fx-text-input" style={{ width: 130 }} value={draft} onChange={(e) => setDraft(e.target.value)}
        placeholder="Add option" onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }} />
      <button type="button" className="fx-login-back" onClick={add} disabled={!draft.trim()}>Add</button>
    </div>
  );
}

/** Org-defined profile fields. Admins define each field AND (for choice fields) the pool of
 *  allowed options; every member then picks their OWN value(s) from that pool. */
function CustomFields({ workspaceId, canManage }: { workspaceId?: string; canManage?: boolean }) {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [vals, setVals] = useState<Record<string, string | string[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newKind, setNewKind] = useState<FieldKind>('text');
  const [newOptions, setNewOptions] = useState<string[]>([]);
  const [editingOpts, setEditingOpts] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    let alive = true;
    api.listFields(workspaceId).then((fs) => {
      if (!alive) return;
      setFields(fs);
      const v: Record<string, string | string[]> = {};
      fs.forEach((f) => { v[f.id] = f.kind === 'tags' ? ((f.value as string[]) || []) : ((f.value as string) || ''); });
      setVals(v);
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [workspaceId]);

  const save = async () => {
    if (!workspaceId) return;
    setSaving(true);
    try { await api.setFieldValues(workspaceId, vals); } catch { /* ignore */ } finally { setSaving(false); }
  };
  const addField = async () => {
    const label = newLabel.trim();
    if (!workspaceId || !label) return;
    if (newKind !== 'text' && newOptions.length === 0) { window.alert('Add at least one option for a choice field.'); return; }
    try {
      const f = await api.createField(workspaceId, { label, kind: newKind, options: newKind !== 'text' ? newOptions : undefined });
      setFields((cur) => [...cur, f]);
      setVals((v) => ({ ...v, [f.id]: f.kind === 'tags' ? [] : '' }));
      setNewLabel(''); setNewKind('text'); setNewOptions([]);
    } catch (e: any) { window.alert(e?.message || 'Could not add the field'); }
  };
  const removeField = async (id: string) => {
    if (!workspaceId) return;
    if (!window.confirm('Remove this field for the whole workspace? Everyone’s values for it go too.')) return;
    try { await api.deleteField(workspaceId, id); setFields((cur) => cur.filter((f) => f.id !== id)); } catch { /* ignore */ }
  };
  const saveOptions = async (id: string, options: string[]) => {
    if (!workspaceId) return;
    if (options.length === 0) { window.alert('A choice field needs at least one option.'); return; }
    setFields((cur) => cur.map((f) => (f.id === id ? { ...f, options } : f)));  // optimistic
    try { await api.updateField(workspaceId, id, { options }); } catch (e: any) { window.alert(e?.message || 'Could not update options.'); }
  };
  const toggleTag = (fid: string, opt: string) => {
    setVals((v) => {
      const cur = (v[fid] as string[]) || [];
      return { ...v, [fid]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });
  };

  if (!workspaceId) return null;
  if (loading) return <div className="fx-set-row-desc">Loading…</div>;
  if (fields.length === 0 && !canManage) {
    return <div className="fx-set-row-desc">Your workspace hasn’t added any custom fields yet.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {fields.map((f) => (
        <div key={f.id} style={{ border: '1px solid var(--fx-hair)', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 650, fontSize: 14, color: 'var(--fx-text)' }}>{f.label}</span>
            <span style={{ fontSize: 11, color: 'var(--fx-muted)', border: '1px solid var(--fx-hair)', borderRadius: 20, padding: '1px 7px' }}>{KIND_LABEL[f.kind]}</span>
            <span style={{ flex: 1 }} />
            {canManage && f.kind !== 'text' && (
              <button type="button" style={fldLink} onClick={() => setEditingOpts((e) => (e === f.id ? null : f.id))}>
                {editingOpts === f.id ? 'Done' : 'Edit options'}
              </button>
            )}
            {canManage && <button type="button" style={{ ...fldLink, color: '#e56a6a' }} onClick={() => removeField(f.id)}>Remove</button>}
          </div>

          {canManage && editingOpts === f.id && f.kind !== 'text' && (
            <OptionEditor options={f.options} onChange={(o) => saveOptions(f.id, o)} />
          )}

          <div style={{ marginTop: 8 }}>
            {f.kind === 'text' && (
              <input className="fx-text-input" style={{ width: '100%', maxWidth: 280, boxSizing: 'border-box' }} value={(vals[f.id] as string) || ''}
                onChange={(e) => setVals((v) => ({ ...v, [f.id]: e.target.value }))} placeholder="Add your value" />
            )}
            {f.kind === 'select' && (
              <Select value={(vals[f.id] as string) || ''} onChange={(val) => setVals((v) => ({ ...v, [f.id]: val }))}
                placeholder="— None —" style={{ width: '100%', maxWidth: 280 }}
                options={[{ value: '', label: '— None —' }, ...f.options.map((o) => ({ value: o, label: o }))]} />
            )}
            {f.kind === 'tags' && (
              f.options.length === 0
                ? <span style={{ fontSize: 12.5, color: 'var(--fx-muted)' }}>No options yet{canManage ? ' — add some above.' : '.'}</span>
                : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {f.options.map((o) => {
                      const sel = ((vals[f.id] as string[]) || []).includes(o);
                      return (
                        <button key={o} type="button" onClick={() => toggleTag(f.id, o)}
                          style={{ ...fldChip, background: sel ? 'var(--fx-accent)' : 'transparent', color: sel ? '#1b1300' : 'var(--fx-text)', borderColor: sel ? 'var(--fx-accent)' : 'var(--fx-hair)', fontWeight: sel ? 700 : 400 }}>
                          {o}
                        </button>
                      );
                    })}
                  </div>
                )
            )}
          </div>
        </div>
      ))}

      {canManage && (
        <div style={{ border: '1px dashed var(--fx-hair)', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--fx-muted)', marginBottom: 8 }}>Add a field</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="fx-text-input" style={{ width: 200 }} value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)} placeholder="Field name (e.g. Department)" />
            <Select value={newKind} onChange={(k) => { setNewKind(k as FieldKind); if (k === 'text') setNewOptions([]); }}
              style={{ width: 160 }}
              options={[{ value: 'text', label: 'Text' }, { value: 'select', label: 'Single choice' }, { value: 'tags', label: 'Multiple tags' }]} />
          </div>
          {newKind !== 'text' && (
            <>
              <div style={{ fontSize: 12, color: 'var(--fx-muted)', marginTop: 10 }}>Options members can choose from:</div>
              <OptionEditor options={newOptions} onChange={setNewOptions} />
            </>
          )}
          <div style={{ marginTop: 10 }}>
            <button type="button" className="fx-login-back" onClick={addField} disabled={!newLabel.trim() || (newKind !== 'text' && newOptions.length === 0)}>Add field</button>
          </div>
        </div>
      )}

      {fields.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <button type="button" className="fx-sendbtn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save my details'}</button>
        </div>
      )}
    </div>
  );
}

export function Settings({ initialTab, onClose, workspaceId, canManageFields }: {
  initialTab: Tab; onClose: () => void; workspaceId?: string; canManageFields?: boolean;
}) {
  const { user, patchUser, logout } = useAuth();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [name, setName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarRef = useRef<HTMLInputElement>(null);
  const tzDefault = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; } })();
  const [mode, setMode] = useState<string>(user?.status_mode || 'active');
  const [emoji, setEmoji] = useState(user?.status_emoji || '');
  const [statusText, setStatusText] = useState(user?.status_text || '');
  const [expiry, setExpiry] = useState('');
  const [autoReply, setAutoReply] = useState(user?.status_auto_reply || '');
  const [savingStatus, setSavingStatus] = useState(false);
  const [tz, setTz] = useState(user?.timezone || tzDefault);
  const [workStart, setWorkStart] = useState(minToTime(user?.work_start_min ?? 540));
  const [workEnd, setWorkEnd] = useState(minToTime(user?.work_end_min ?? 1020));
  const [workDays, setWorkDays] = useState<number[]>(
    (user?.work_days ?? '1,2,3,4,5').split(',').map((d) => Number(d.trim())).filter((n) => !Number.isNaN(n)),
  );
  const [savingTz, setSavingTz] = useState(false);
  if (!user) return null;

  const saveName = async () => {
    if (!name.trim() || name.trim() === user.name) return;
    setSavingName(true);
    try {
      patchUser(await api.updateProfile({ name: name.trim() }));
    } finally {
      setSavingName(false);
    }
  };

  const onAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const att = await api.uploadFile(file);
      patchUser(await api.updateProfile({ avatar_url: att.url }));
    } catch (ex: any) {
      window.alert(ex?.message || 'Avatar upload failed');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const saveStatus = async () => {
    setSavingStatus(true);
    try {
      patchUser(await api.updateMe({
        status_emoji: emoji.trim() || null,
        status_text: statusText.trim() || null,
        status_mode: mode as any,
        status_expires_at: expiryToIso(expiry),
        status_auto_reply: mode !== 'active' ? (autoReply.trim() || null) : null,
      }));
    } finally {
      setSavingStatus(false);
    }
  };
  const clearStatus = async () => {
    setMode('active'); setEmoji(''); setStatusText(''); setAutoReply(''); setExpiry('');
    patchUser(await api.updateMe({ clear_status: true }));
  };
  const saveTz = async () => {
    setSavingTz(true);
    try {
      patchUser(await api.updateMe({
        timezone: tz.trim() || null,
        work_start_min: timeToMin(workStart),
        work_end_min: timeToMin(workEnd),
        work_days: workDays.slice().sort((a, b) => a - b).join(','),
      }));
    } finally {
      setSavingTz(false);
    }
  };
  const toggleDay = (d: number) =>
    setWorkDays((ds) => (ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d]));

  return (
    <div className="fx-settings-backdrop" onClick={onClose}>
      <div className="fx-settings" onClick={(e) => e.stopPropagation()}>
        <div className="fx-set-nav">
          <div className="fx-set-nav-head">
            <div className="fx-set-avatar" style={avatarStyle(user)}>
              {user.avatar_url ? '' : (user.name || user.email)[0]?.toUpperCase()}
            </div>
            Settings
          </div>
          <div className="fx-set-nav-items">
            {TABS.map((t) => (
              <button
                type="button"
                key={t.key}
                className={`fx-set-navitem${tab === t.key ? ' is-active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                <Icon name={t.icon} size={17} /> {t.label}
              </button>
            ))}
          </div>
          <button type="button" className="fx-set-logout" onClick={logout}>
            <Icon name="logout" size={17} /> Sign out
          </button>
        </div>

        <div className="fx-set-content">
          <div className="fx-set-content-head">
            <div className="fx-set-content-title">{TABS.find((t) => t.key === tab)?.label}</div>
            <button type="button" className="fx-set-close" onClick={onClose} aria-label="Close">✕</button>
          </div>

          <div className="fx-set-body">
            {tab === 'account' && (
              <div className="fx-set-section">
                <div className="fx-set-label">Identity</div>
                <div className="fx-set-card">
                  <div className="fx-set-row">
                    <button
                      type="button"
                      className="fx-acct-avatar fx-avatar-btn"
                      style={avatarStyle(user)}
                      title="Change photo"
                      onClick={() => avatarRef.current?.click()}
                    >
                      {user.avatar_url ? '' : (user.name || user.email)[0]?.toUpperCase()}
                    </button>
                    <input ref={avatarRef} type="file" accept="image/*" hidden onChange={onAvatarPick} />
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Profile photo</div>
                      <div className="fx-set-row-desc">
                        {uploadingAvatar ? 'Uploading…' : 'Click the avatar to upload a new photo.'}
                      </div>
                    </div>
                  </div>
                  <div className="fx-set-row">
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Display name</div>
                      <div className="fx-set-row-desc">Shown on your messages and in the member list.</div>
                    </div>
                    <div className="fx-set-row-ctrl">
                      <input className="fx-text-input" value={name} onChange={(e) => setName(e.target.value)} />
                      <button type="button" className="fx-btn" onClick={saveName} disabled={savingName}>
                        {savingName ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                  <div className="fx-set-row">
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Email</div>
                      <div className="fx-set-row-desc">Your sign-in address.</div>
                    </div>
                    <div className="fx-set-row-ctrl">
                      <span className="fx-acct-value">{user.email}</span>
                    </div>
                  </div>
                  <div className="fx-set-row">
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Status</div>
                      <div className="fx-set-row-desc">Set from the account menu (avatar → Update your status).</div>
                    </div>
                    <div className="fx-set-row-ctrl">
                      <span className="fx-acct-value">
                        {user.status_emoji || user.status_text
                          ? `${user.status_emoji || ''} ${user.status_text || ''}`
                          : '—'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="fx-set-label">Your details</div>
                <div className="fx-set-card">
                  {/* Everyone fills their own values here. Admins DEFINE the fields + option
                      pools in the Admin panel (More → Admin → Member fields). */}
                  <CustomFields workspaceId={workspaceId} canManage={false} />
                </div>
              </div>
            )}

            {tab === 'status' && (
              <div className="fx-set-section">
                <div className="fx-set-label">Availability</div>
                <div className="fx-set-card">
                  <div className="fx-set-row">
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Mode</div>
                      <div className="fx-set-row-desc">Focus / Async / DND tell people you're heads-down and turn on a one-time DM auto-reply.</div>
                    </div>
                    <div className="fx-cc-seg">
                      {(['active', 'focus', 'async', 'dnd'] as const).map((m) => (
                        <button type="button" key={m} className={`fx-cc-seg-btn${mode === m ? ' is-active' : ''}`} onClick={() => setMode(m)}>
                          {MODE_LABEL[m]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="fx-set-row">
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Status</div>
                      <div className="fx-set-row-desc">A short line others see — e.g. “Deep work till 2pm”.</div>
                    </div>
                    <div className="fx-set-row-ctrl">
                      <input className="fx-text-input" style={{ width: 54, textAlign: 'center' }} value={emoji} onChange={(e) => setEmoji(e.target.value)} placeholder="💬" maxLength={4} />
                      <input className="fx-text-input" value={statusText} onChange={(e) => setStatusText(e.target.value)} placeholder="What's your status?" />
                    </div>
                  </div>
                  <div className="fx-set-row">
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Clear after</div>
                      <div className="fx-set-row-desc">Automatically resets your status.</div>
                    </div>
                    <div className="fx-set-row-ctrl">
                      <Select value={expiry} onChange={setExpiry} ariaLabel="Clear status after" style={{ minWidth: 150 }}
                        options={[
                          { value: '', label: "Don't clear" },
                          { value: '30', label: '30 minutes' },
                          { value: '60', label: '1 hour' },
                          { value: '240', label: '4 hours' },
                          { value: 'today', label: 'Today (till 6pm)' },
                        ]} />
                    </div>
                  </div>
                  {mode !== 'active' && (
                    <div className="fx-set-row">
                      <div className="fx-set-row-main">
                        <div className="fx-set-row-title">DM auto-reply</div>
                        <div className="fx-set-row-desc">Sent once when someone DMs you during this window.</div>
                      </div>
                      <div className="fx-set-row-ctrl">
                        <textarea className="fx-text-input" rows={2} value={autoReply} onChange={(e) => setAutoReply(e.target.value)} placeholder="Heads-down till 2pm — I'll reply after. If it's urgent, call." />
                      </div>
                    </div>
                  )}
                  <div className="fx-set-row">
                    <div className="fx-set-row-main" />
                    <div className="fx-set-row-ctrl">
                      <button type="button" className="fx-btn" onClick={clearStatus}>Clear status</button>
                      <button type="button" className="fx-sendbtn" onClick={saveStatus} disabled={savingStatus}>{savingStatus ? 'Saving…' : 'Save status'}</button>
                    </div>
                  </div>
                </div>

                <div className="fx-set-label">Time zone &amp; working hours</div>
                <div className="fx-set-card">
                  <div className="fx-set-row">
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Time zone</div>
                      <div className="fx-set-row-desc">Others see your local time inline; used for “find a time”.</div>
                    </div>
                    <div className="fx-set-row-ctrl">
                      <input className="fx-text-input" value={tz} onChange={(e) => setTz(e.target.value)} placeholder="America/New_York" spellCheck={false} />
                    </div>
                  </div>
                  <div className="fx-set-row">
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Working hours</div>
                      <div className="fx-set-row-desc">When you're usually reachable, in your local time.</div>
                    </div>
                    <div className="fx-set-row-ctrl">
                      <DateTimePicker mode="time" value={workStart} onChange={setWorkStart} style={{ width: 96 }} />
                      <span style={{ opacity: 0.6 }}>to</span>
                      <DateTimePicker mode="time" value={workEnd} onChange={setWorkEnd} style={{ width: 96 }} />
                    </div>
                  </div>
                  <div className="fx-set-row">
                    <div className="fx-set-row-main">
                      <div className="fx-set-row-title">Working days</div>
                    </div>
                    <div className="fx-set-row-ctrl fx-day-row">
                      {DAY_LETTERS.map((d, i) => (
                        <button type="button" key={i} className={`fx-day-btn${workDays.includes(i) ? ' is-active' : ''}`} onClick={() => toggleDay(i)} title={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="fx-set-row">
                    <div className="fx-set-row-main" />
                    <div className="fx-set-row-ctrl">
                      <button type="button" className="fx-sendbtn" onClick={saveTz} disabled={savingTz}>{savingTz ? 'Saving…' : 'Save time zone'}</button>
                    </div>
                  </div>
                </div>
                <div className="fx-set-label">Connected calendars</div>
                <div className="fx-set-card">
                  <CalendarConnections />
                </div>
              </div>
            )}

            {tab === 'about' && (
              <div className="fx-set-section">
                <div className="fx-about-head">
                  <div className="fx-about-logo">F</div>
                  <div>
                    <div className="fx-about-name">
                      FxM Chat <span className="fx-about-ver">v1 · option B</span>
                    </div>
                    <div className="fx-about-tag">Our own stack — Postgres + FastAPI + Centrifugo. No Matrix.</div>
                  </div>
                </div>
                <div className="fx-about-credits">
                  <p>Realtime messaging, DMs, agents (bot API), reactions, files, and LiveKit calls.</p>
                  <p>Built for FxM · getfxm.ai</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
